/**
 * BulkSendToSymfoniaModal — „Wyślij do FK" z LISTY faktur (akcja zbiorcza / prawy klik / kebab).
 *
 * Lustro dwukrokowego przebiegu z karty faktury (`SendToSymfoniaButton`), bo wysyłka do Symfonii
 * jest NIEODWRACALNA (WebAPI FK nie ma ani UPDATE, ani DELETE dokumentu):
 *   1. otwarcie okna = sprawdzenie NA PRÓBĘ każdej zaznaczonej faktury (`dryRun: true`) —
 *      w Symfonii nic nie powstaje;
 *   2. okno pokazuje wynik per faktura: gotowa / uwagi Symfonii / braki po naszej stronie
 *      (np. brak dekretu) / już wysłana / błąd połączenia — z przyciskiem „Otwórz fakturę",
 *      żeby dało się od razu przejść do niej i ją poprawić (np. zadekretować);
 *   3. dopiero jawne „Wyślij N do Symfonii" wystawia dokumenty — WYŁĄCZNIE gotowe.
 *
 * Poprzednio akcja szła generycznym rozgłoszeniem bez body → backend domyślnie robił samo
 * sprawdzenie na próbę, a lista pokazywała „Gotowe" — nic nie trafiało do Symfonii.
 *
 * Świadome zawężenia wobec karty (decyzja: zbiorczo tylko „czyste" przypadki):
 *   - faktura z BŁĘDAMI Symfonii nie jest wysyłana zbiorczo (na karcie da się „wysłać mimo
 *     błędów" po przeczytaniu uwag — to decyzja per dokument);
 *   - faktura już wysłana jest pomijana (ponowienie = świadoma zgoda na karcie, `allowResend`).
 *
 * Wywołania idą z ograniczoną równoległością (`CONCURRENCY`) — każde otwiera sesję FK, a Symfonia
 * ma limit sesji. Wystawienie leci SEKWENCYJNIE (nieodwracalne, kolejność numeracji w FK).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { AlertTriangle, CheckCircle2, ExternalLink, Info, Loader2, XCircle } from 'lucide-react';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { recordLockReason } from '@/lib/documents/record-lock-reason';
import { localIsoDate } from '@/lib/local-date';

/** Ile sprawdzeń na próbę naraz — każde otwiera sesję FK (limit po stronie Symfonii). */
const CONCURRENCY = 3;

interface FkMessage {
  Type: string | number;
  Message: string;
}

interface OwnProblem {
  field: string;
  message: string;
}

interface SendResponse {
  kind: 'dry_run' | 'sent';
  messages?: FkMessage[];
  warnings?: OwnProblem[];
  fkDocumentId?: string | null;
  fkDocumentNumber?: string | null;
}

/** Werdykt sprawdzenia na próbę jednej faktury. */
export type FkCheckStatus =
  | 'checking'
  | 'ready'
  | 'fk_errors'
  | 'not_ready'
  | 'already_sent'
  | 'failed';

interface RowState {
  id: string;
  label: string;
  status: FkCheckStatus;
  /** Blokady (nasze braki / błędy Symfonii / komunikat awarii). */
  problems: string[];
  /** Ostrzeżenia — nie blokują wysyłki. */
  warnings: string[];
  /** Po wysyłce: numer w FK albo powody odmowy wystawienia. */
  sentNumber?: string | null;
  sendError?: string[];
}

export interface BulkSendToSymfoniaModalProps {
  open: boolean;
  onClose: () => void;
  /** Zaznaczone faktury — `label` = numer widoczny dla usera. */
  documents: Array<{ id: string; label: string }>;
  /** Ścieżka karty faktury (do „Otwórz fakturę"). */
  detailPath: (id: string) => string;
  /** Po wysłaniu co najmniej jednej faktury: odśwież listę + wyczyść zaznaczenie. */
  onDone: () => void;
  testId?: string;
}

function isFkError(m: FkMessage): boolean {
  return m.Type === 2 || String(m.Type).toLowerCase() === 'error';
}
function isFkWarning(m: FkMessage): boolean {
  return m.Type === 1 || String(m.Type).toLowerCase() === 'warning';
}

function errorBody(err: unknown): Record<string, unknown> | null {
  if (isAxiosError(err) && err.response?.data && typeof err.response.data === 'object') {
    return err.response.data as Record<string, unknown>;
  }
  return null;
}

/**
 * Powody odmowy z odpowiedzi trasy wysyłki — wspólne dla sprawdzenia na próbę i wystawienia:
 * braki po naszej stronie (`problems`, 422), uwagi Symfonii (`messages`, 502) albo gotowy
 * komunikat (`message`). Bez powtórzeń. Pusta lista = odmowa bez podanej przyczyny.
 */
function errorReasons(body: Record<string, unknown> | null): string[] {
  if (Array.isArray(body?.problems) && body.problems.length > 0) {
    return [...new Set((body.problems as OwnProblem[]).map((p) => p.message))];
  }
  if (Array.isArray(body?.messages) && body.messages.length > 0) {
    return [...new Set((body.messages as FkMessage[]).map((m) => m.Message))];
  }
  return typeof body?.message === 'string' ? [body.message] : [];
}

/** Proste `map` z limitem równoległości — kolejność wyników = kolejność wejścia. */
async function mapWithLimit<T, R>(
  items: T[],
  limit: number,
  fn: (item: T) => Promise<R>,
): Promise<R[]> {
  const out: R[] = new Array(items.length);
  let next = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const i = next++;
      out[i] = await fn(items[i]!);
    }
  });
  await Promise.all(workers);
  return out;
}

export function BulkSendToSymfoniaModal({
  open,
  onClose,
  documents,
  detailPath,
  onDone,
  testId = 'bulk-fk-send-modal',
}: BulkSendToSymfoniaModalProps) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();

  const [rows, setRows] = useState<RowState[]>([]);
  const [phase, setPhase] = useState<'checking' | 'review' | 'sending' | 'done'>('checking');
  // Bieg sprawdzenia przypięty do otwarcia — zamknięcie/ponowne otwarcie unieważnia stare wyniki.
  const runRef = useRef(0);

  const genericError = t('tenant.bulkFkSend.genericError');
  const sendGenericError = t('tenant.bulkFkSend.sendGenericError');

  const checkOne = useCallback(
    async (doc: { id: string; label: string }): Promise<RowState> => {
      const base = { id: doc.id, label: doc.label, problems: [], warnings: [] };
      try {
        const res = await api.post<SendResponse>(`/documents/${doc.id}/send-to-symfonia`, {
          dryRun: true,
          allowResend: false,
          // „Dziś" z zegara i strefy przeglądarki — serwer liczy w UTC, więc sam go nie zna.
          clientDate: localIsoDate(),
        });
        const messages = res.data.messages ?? [];
        const errors = [...new Set(messages.filter(isFkError).map((m) => m.Message))];
        // Bez powtórzeń — backend potrafi zwrócić to samo ostrzeżenie raz per zapis dekretu
        // (np. „konto 202 bez kontrahenta w analityce" ×6), co w zbiorczym widoku zalewa listę.
        const warnings = [
          ...new Set([
            ...messages.filter(isFkWarning).map((m) => m.Message),
            ...(res.data.warnings ?? []).map((w) => w.message),
          ]),
        ];
        return {
          ...base,
          status: errors.length > 0 ? 'fk_errors' : 'ready',
          problems: errors,
          warnings,
        };
      } catch (err) {
        // Na fakturze trwa zadanie przycisku SQL (409 `RECORD_LOCKED_BY_RUN`) — wysyłka zapisałaby na niej
        // ślad, więc serwer odmawia; powód z opisem zadania zamiast ogólnego błędu.
        const lockReason = recordLockReason(err, t, i18n);
        if (lockReason !== null) return { ...base, status: 'failed', problems: [lockReason] };
        const body = errorBody(err);
        const code = typeof body?.error === 'string' ? body.error : undefined;
        const reasons = errorReasons(body);
        if (code === 'NOT_READY_TO_SEND' && Array.isArray(body?.problems)) {
          const problems = body.problems as OwnProblem[];
          const onlyAlreadySent =
            problems.length > 0 && problems.every((p) => p.field === 'alreadySent');
          return {
            ...base,
            status: onlyAlreadySent ? 'already_sent' : 'not_ready',
            problems: reasons,
          };
        }
        if (code === 'FK_REJECTED') {
          // Odmowa BEZ uwag = awaria wywołania po stronie FK, nie werdykt walidatora.
          return reasons.length > 0
            ? { ...base, status: 'fk_errors', problems: reasons }
            : { ...base, status: 'failed', problems: [t('tenant.bulkFkSend.fkFailure')] };
        }
        return {
          ...base,
          status: 'failed',
          problems: reasons.length > 0 ? reasons : [genericError],
        };
      }
    },
    [t, i18n, genericError],
  );

  useEffect(() => {
    if (!open) return;
    const run = ++runRef.current;
    setPhase('checking');
    setRows(documents.map((d) => ({ ...d, status: 'checking', problems: [], warnings: [] })));
    void mapWithLimit(documents, CONCURRENCY, checkOne).then((checked) => {
      if (runRef.current !== run) return;
      setRows(checked);
      setPhase('review');
    });
    // `documents` zmienia tożsamość przy każdym renderze rodzica — kluczem jest zbiór ID.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, documents.map((d) => d.id).join(',')]);

  const readyRows = rows.filter((r) => r.status === 'ready');

  const sendReady = async () => {
    const run = runRef.current;
    setPhase('sending');
    let ok = 0;
    for (const row of readyRows) {
      if (runRef.current !== run) return;
      try {
        const res = await api.post<SendResponse>(`/documents/${row.id}/send-to-symfonia`, {
          dryRun: false,
          allowResend: false,
          clientDate: localIsoDate(),
        });
        ok++;
        setRows((prev) =>
          prev.map((r) =>
            r.id === row.id
              ? { ...r, sentNumber: res.data.fkDocumentNumber ?? res.data.fkDocumentId ?? '' }
              : r,
          ),
        );
      } catch (err) {
        // Powód odmowy wystawienia (np. Symfonia odrzuciła dokument, albo w międzyczasie ktoś
        // wysłał go z karty → „już wysłana") — nie ogólnik ze sprawdzania; operacja nieodwracalna.
        // Zadanie przycisku SQL, które ruszyło po sprawdzeniu na próbę (409 `RECORD_LOCKED_BY_RUN`), też
        // dostaje swój opis.
        const lockReason = recordLockReason(err, t, i18n);
        const reasons = lockReason !== null ? [lockReason] : errorReasons(errorBody(err));
        const sendError = reasons.length > 0 ? reasons : [sendGenericError];
        setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, sendError } : r)));
      }
    }
    if (runRef.current !== run) return;
    setPhase('done');
    if (ok > 0) onDone();
    const failed = readyRows.length - ok;
    toast.show({
      variant: failed > 0 ? 'error' : 'success',
      title:
        failed > 0
          ? t('tenant.bulkFkSend.toast.partial', { ok, failed })
          : t('tenant.bulkFkSend.toast.done', { count: ok }),
    });
  };

  const close = () => {
    runRef.current++; // unieważnij trwający bieg
    onClose();
  };

  const openInvoice = (id: string) => {
    close();
    navigate(detailPath(id));
  };

  const statusBadge = (row: RowState) => {
    if (row.sentNumber !== undefined) {
      return (
        <Badge color="green" size="sm">
          {t('tenant.bulkFkSend.status.sent')}
        </Badge>
      );
    }
    if (row.sendError) {
      return (
        <Badge color="red" size="sm">
          {t('tenant.bulkFkSend.status.sendFailed')}
        </Badge>
      );
    }
    const map: Record<FkCheckStatus, { color: 'blue' | 'green' | 'amber' | 'red' | 'gray' }> = {
      checking: { color: 'gray' },
      ready: { color: 'green' },
      fk_errors: { color: 'red' },
      not_ready: { color: 'amber' },
      already_sent: { color: 'blue' },
      failed: { color: 'red' },
    };
    return (
      <Badge color={map[row.status].color} size="sm">
        {t(`tenant.bulkFkSend.status.${row.status}`)}
      </Badge>
    );
  };

  const statusIcon = (row: RowState) => {
    if (
      row.status === 'checking' ||
      (phase === 'sending' &&
        row.status === 'ready' &&
        row.sentNumber === undefined &&
        !row.sendError)
    ) {
      return <Loader2 className="h-4 w-4 animate-spin text-text-tertiary" aria-hidden="true" />;
    }
    if (row.sentNumber !== undefined || (row.status === 'ready' && !row.sendError)) {
      return <CheckCircle2 className="h-4 w-4 text-green-600" aria-hidden="true" />;
    }
    if (row.status === 'already_sent') {
      return <Info className="h-4 w-4 text-blue-600" aria-hidden="true" />;
    }
    if (row.status === 'not_ready') {
      return <AlertTriangle className="h-4 w-4 text-amber-600" aria-hidden="true" />;
    }
    return <XCircle className="h-4 w-4 text-red-600" aria-hidden="true" />;
  };

  const footer =
    phase === 'done' ? (
      <div className="flex justify-end">
        <Button variant="secondary" onClick={close} data-testid={`${testId}-close-btn`}>
          {t('common.close')}
        </Button>
      </div>
    ) : (
      <div className="flex justify-end gap-2">
        <Button
          variant="secondary"
          onClick={close}
          disabled={phase === 'sending'}
          data-testid={`${testId}-cancel-btn`}
        >
          {t('common.cancel')}
        </Button>
        <Button
          // Wysyłka jest nieodwracalna — wariant ostrzegawczy, jak na karcie faktury.
          variant="danger"
          onClick={() => void sendReady()}
          disabled={phase !== 'review' || readyRows.length === 0}
          loading={phase === 'sending'}
          data-testid={`${testId}-submit-btn`}
        >
          {t('tenant.bulkFkSend.submit', { count: readyRows.length })}
        </Button>
      </div>
    );

  return (
    <Modal
      open={open}
      onClose={phase === 'sending' ? () => undefined : close}
      title={t('tenant.bulkFkSend.title')}
      description={t('tenant.bulkFkSend.description')}
      size="lg"
      footer={footer}
    >
      <div className="flex flex-col gap-3 text-sm" data-testid={testId}>
        <p className="text-text-secondary" data-testid={`${testId}-summary`}>
          {phase === 'checking'
            ? t('tenant.bulkFkSend.checking', { count: documents.length })
            : t('tenant.bulkFkSend.summary', {
                ready: readyRows.length,
                total: documents.length,
              })}
        </p>

        <ul className="flex max-h-96 flex-col gap-1.5 overflow-auto" data-testid={`${testId}-rows`}>
          {rows.map((row) => {
            const blocked =
              row.status !== 'ready' && row.status !== 'checking' && phase !== 'checking';
            return (
              <li
                key={row.id}
                className="rounded-md border border-border-primary p-2.5"
                data-testid={`${testId}-row-${row.id}`}
                data-status={row.status}
              >
                <div className="flex items-center gap-2">
                  {statusIcon(row)}
                  <span className="min-w-0 flex-1 truncate font-medium text-text-primary">
                    {row.label}
                  </span>
                  {statusBadge(row)}
                  {(blocked || row.sendError) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      rightIcon={<ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />}
                      onClick={() => openInvoice(row.id)}
                      disabled={phase === 'sending'}
                      data-testid={`${testId}-open-${row.id}`}
                    >
                      {t('tenant.bulkFkSend.openInvoice')}
                    </Button>
                  )}
                </div>
                {row.sentNumber ? (
                  <p className="mt-1 pl-6 text-xs text-text-secondary">
                    {t('tenant.bulkFkSend.sentAs', { number: row.sentNumber })}
                  </p>
                ) : null}
                {row.sendError ? (
                  <ul
                    className="mt-1 list-disc space-y-0.5 pl-10 text-xs text-red-700 dark:text-red-300"
                    data-testid={`${testId}-send-error-${row.id}`}
                  >
                    {row.sendError.map((e, i) => (
                      <li key={i}>{e}</li>
                    ))}
                  </ul>
                ) : null}
                {row.problems.length > 0 && (
                  <ul
                    className={`mt-1 list-disc space-y-0.5 pl-10 text-xs ${
                      row.status === 'already_sent'
                        ? 'text-text-secondary'
                        : 'text-red-700 dark:text-red-300'
                    }`}
                  >
                    {row.problems.map((p, i) => (
                      <li key={i}>{p}</li>
                    ))}
                  </ul>
                )}
                {row.warnings.length > 0 && (
                  <ul className="mt-1 list-disc space-y-0.5 pl-10 text-xs text-amber-700 dark:text-amber-300">
                    {row.warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>

        {phase === 'review' && readyRows.length < documents.length && (
          <p className="text-xs text-text-tertiary" data-testid={`${testId}-skip-hint`}>
            {t('tenant.bulkFkSend.skipHint')}
          </p>
        )}
      </div>
    </Modal>
  );
}
